// Today-tab Triggers section. Manual-only self-report inputs that the
// wearable can't capture: Caffeine, Alcohol, Stress.
//
// Sleep and Exercise live in the Wearable Snapshot and are NOT re-entered
// here. The Insights tab still correlates sleep/exercise with outcomes by
// pulling them directly from wearable_metrics (see triggerCorrelations.ts).
import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { useServerFn } from "@tanstack/react-start";
import { supabase } from "@/integrations/supabase/client";
import { listWearableConnections } from "@/lib/wearables.functions";
import { localDateKey } from "./dailyLogs";
import {
  fetchTriggersForDate,
  upsertTrigger,
  type DailyTriggerRow,
  type TriggerField,
} from "./triggers";
import { SignInForWearablesBanner } from "@/knowmeno/SignInForWearablesBanner";
import { classifyWearableError } from "@/knowmeno/wearableErrors";

// Brand tokens — kept in sync with KnowMenoApp.tsx.
const TD = "#4B1F4F";
const GD = "#6E4B71";
const GR = "#6E4B71";
const GL = "#FAF7F5";
const WH = "#ffffff";
const RO = "#FF5C7A";
const RL = "#FFE4EA";

function Stepper({
  value,
  min,
  max,
  step = 1,
  onChange,
  disabled,
  ariaLabel,
}: {
  value: number;
  min: number;
  max: number;
  step?: number;
  onChange: (n: number) => void;
  disabled?: boolean;
  ariaLabel: string;
}) {
  const dec = () => onChange(Math.max(min, value - step));
  const inc = () => onChange(Math.min(max, value + step));
  const atMin = value <= min;
  const atMax = value >= max;
  const btn = (active: boolean) => ({
    width: 34,
    height: 34,
    borderRadius: 10,
    border: `1.5px solid ${active ? "#E8D5DA" : "#F0E4E7"}`,
    background: active ? WH : GL,
    color: active ? TD : "#C9B4CC",
    fontSize: 18,
    fontWeight: 500,
    cursor: active && !disabled ? "pointer" : "not-allowed",
    padding: 0,
    lineHeight: 1,
  });
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 8 }} aria-label={ariaLabel}>
      <button type="button" onClick={dec} disabled={atMin || disabled} style={btn(!atMin && !disabled)}>−</button>
      <div
        style={{
          minWidth: 36,
          textAlign: "center",
          fontSize: 16,
          fontWeight: 600,
          color: TD,
          fontVariantNumeric: "tabular-nums",
        }}
      >
        {value}
      </div>
      <button type="button" onClick={inc} disabled={atMax || disabled} style={btn(!atMax && !disabled)}>+</button>
    </div>
  );
}

function ScalePicker<T extends number>({
  value,
  options,
  onChange,
  ariaLabel,
}: {
  value: T | null;
  options: { v: T; label: string }[];
  onChange: (n: T) => void;
  ariaLabel: string;
}) {
  return (
    <div style={{ display: "flex", gap: 6 }} role="radiogroup" aria-label={ariaLabel}>
      {options.map(opt => {
        const sel = value === opt.v;
        return (
          <button
            key={String(opt.v)}
            type="button"
            role="radio"
            aria-checked={sel}
            onClick={() => onChange(opt.v)}
            style={{
              flex: 1,
              padding: "9px 4px",
              borderRadius: 10,
              border: `1.5px solid ${sel ? RO : "#E8D5DA"}`,
              background: sel ? RL : WH,
              color: sel ? RO : GD,
              fontSize: 12,
              fontWeight: sel ? 600 : 500,
              cursor: "pointer",
              lineHeight: 1.3,
            }}
          >
            {opt.label}
          </button>
        );
      })}
    </div>
  );
}

function Row({
  emoji,
  title,
  subtitle,
  children,
}: {
  emoji: string;
  title: string;
  subtitle?: string;
  children: React.ReactNode;
}) {
  return (
    <div
      style={{
        background: WH,
        border: "1px solid #F0E4E7",
        borderRadius: 14,
        padding: "12px 14px",
        marginBottom: 10,
      }}
    >
      <div style={{ display: "flex", alignItems: "center", marginBottom: 10, gap: 8 }}>
        <div style={{ fontSize: 18 }}>{emoji}</div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 14, fontWeight: 500, color: TD, lineHeight: 1.2 }}>{title}</div>
          {subtitle && (
            <div style={{ fontSize: 11, color: GR, marginTop: 2 }}>{subtitle}</div>
          )}
        </div>
      </div>
      {children}
    </div>
  );
}

export function TriggersCard({ onGoToInsights }: { onGoToInsights?: (sectionId?: string) => void } = {}) {
  const [uid, setUid] = useState<string | null>(null);
  const [row, setRow] = useState<DailyTriggerRow | null>(null);
  const [loaded, setLoaded] = useState(false);
  // When the user has any connected/pending wearable, stress is auto-derived
  // from HRV (hrv_proxy) by the Junction webhook and the manual slider is
  // hidden. Non-wearable users keep the manual slider exactly as before.
  const [hasWearable, setHasWearable] = useState<boolean>(false);
  const [authMissing, setAuthMissing] = useState(false);
  const listConnections = useServerFn(listWearableConnections);
  const dateKey = localDateKey();

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data: sess } = await supabase.auth.getUser();
        const id = sess?.user?.id || null;
        if (!id || cancelled) { setLoaded(true); return; }
        setUid(id);
        const [r, conns] = await Promise.all([
          fetchTriggersForDate(id, dateKey),
          listConnections().catch((e) => {
            const { showAuthBanner } = classifyWearableError(e);
            if (showAuthBanner) setAuthMissing(true);
            return { connections: [] as any[] };
          }),
        ]);
        if (cancelled) return;
        setRow(r);
        const any = (conns?.connections ?? []).some(
          (c: any) => c.status === "connected" || c.status === "pending",
        );
        setHasWearable(any);
        setLoaded(true);
      } catch (e) {
        console.warn("[Triggers] load failed", e);
        if (!cancelled) setLoaded(true);
      }
    })();
    return () => { cancelled = true; };
  }, [dateKey, listConnections]);

  const save = useCallback(async (field: TriggerField, value: number | null) => {
    if (!uid) return;
    // Optimistic update — these fields are pure manual self-report, so the
    // source is always "manual".
    setRow(prev => {
      const base: DailyTriggerRow = prev ?? {
        id: "", user_id: uid, log_date: dateKey,
        caffeine_count: 0, caffeine_source: "manual",
        alcohol_count: 0, alcohol_source: "manual",
        exercise_minutes: 0, exercise_source: "manual",
        sleep_hours: null, sleep_quality: null, sleep_source: "manual",
        stress_level: null, stress_source: "manual",
        created_at: "", updated_at: "",
      };
      return { ...base, [field]: value } as DailyTriggerRow;
    });
    const attempt = async (retry: boolean) => {
      try {
        await upsertTrigger(uid, dateKey, field, value, "manual");
      } catch (e) {
        if (retry) { await attempt(false); return; }
        console.warn("[Triggers] save failed", e);
        toast.error("Couldn't save. Tap to retry.", {
          action: { label: "Retry", onClick: () => { void attempt(false); } },
        });
      }
    };
    void attempt(true);
  }, [uid, dateKey]);

  const r = row;
  const caffeine = r?.caffeine_count ?? 0;
  const alcohol = r?.alcohol_count ?? 0;
  const stress = r?.stress_level ?? null;

  const today = new Date().toLocaleDateString("en-US", { weekday: "long", month: "short", day: "numeric" });

  return (
    <div
      style={{
        background: WH,
        borderRadius: 20,
        border: "0.5px solid #E8D5DA",
        padding: 18,
        marginBottom: 14,
      }}
    >
      <div style={{ fontSize: 16, fontWeight: 500, color: TD, marginBottom: 2 }}>Triggers</div>
      <div style={{ fontSize: 13, color: GR, lineHeight: 1.5 }}>
        Self-report factors your wearable can't see. Sleep and activity are tracked in your Wearable Snapshot.
      </div>
      <div style={{ fontSize: 11, color: GR, marginBottom: 14, marginTop: 2 }}>{today}</div>

      {authMissing && (
        <div style={{ marginBottom: 14 }}>
          <SignInForWearablesBanner />
        </div>
      )}

      {!loaded ? (
        <div style={{ fontSize: 13, color: GR, padding: "12px 0" }}>Loading…</div>
      ) : (
        <>
          <Row emoji="☕" title="Caffeine" subtitle="Cups today">
            <Stepper
              value={caffeine}
              min={0}
              max={20}
              onChange={n => save("caffeine_count", n)}
              ariaLabel="Caffeine cups today"
            />
          </Row>

          <Row emoji="🍷" title="Alcohol" subtitle="Drinks today">
            <Stepper
              value={alcohol}
              min={0}
              max={20}
              onChange={n => save("alcohol_count", n)}
              ariaLabel="Alcohol drinks today"
            />
          </Row>

          {!hasWearable && (
            <Row emoji="😰" title="Stress level">
              <ScalePicker
                value={stress as 1 | 2 | 3 | 4 | null}
                options={[
                  { v: 1, label: "Low" },
                  { v: 2, label: "Mild" },
                  { v: 3, label: "Moderate" },
                  { v: 4, label: "High" },
                ]}
                onChange={n => save("stress_level", n)}
                ariaLabel="Stress level"
              />
            </Row>
          )}
        </>
      )}
      {(() => {
        const hasAny = !!r && (
          (r.caffeine_count ?? 0) > 0 ||
          (r.alcohol_count ?? 0) > 0 ||
          r.stress_level != null
        );
        if (!hasAny) return null;
        return (
          <button
            onClick={() => onGoToInsights && onGoToInsights()}
            style={{
              marginTop: 14,
              width: "100%",
              background: "none",
              border: "none",
              borderTop: "0.5px solid #E8D5DA",
              padding: "12px 0 0",
              fontSize: 12,
              color: TD,
              fontWeight: 500,
              cursor: "pointer",
              textAlign: "center",
              fontFamily: "inherit",
            }}
          >
            ✨ See how your triggers correlate with your score →
          </button>
        );
      })()}
    </div>
  );
}
